import { Query } from '@dreryk/command-query-api/command-query-api';
import { useCallback, useMemo } from 'react';
import { useAsyncFn, useUpdateEffect } from 'react-use';

import { performCqrsQuerySearch, useCqrsExists } from '@dreryk/command-query-react-common';
import { Bean, PromiseVoidFunction, TranslatableText, useFormController } from '@dreryk/react-common';
import { ActionInput, LoaderSpinner, Message, MessageType, TestId } from '@dreryk/react-components';

import { formsTemplatesTranslationKeys } from '../translations';

import { FormsTemplatesListNoSearchResultsEmptyStatePlaceholder } from './FormsTemplatesListNoSearchResultsEmptyStatePlaceholder';
import { StyledFormsTemplatesSearchInput } from './FormsTemplatesSearchInput.styled';

const FORMS_TEMPLATES_SEARCH_DEBOUNCE_TIME_IN_MS = 300;

type FormsTemplatesSearchChildrenRenderProps<ITEM extends Bean> = {
   templates: ITEM[];
   refetch: PromiseVoidFunction;
   loading: boolean;
   templatesExist: boolean;
};

type FormsTemplatesSearchProps<ITEM extends Bean> = {
   readonly children: (props: FormsTemplatesSearchChildrenRenderProps<ITEM>) => JSX.Element;
   readonly queryCreator: (searchText?: string | null) => Query<ITEM>;
   readonly testId: TestId;
   readonly placeholder: TranslatableText;
};

// eslint-disable-next-line max-lines-per-function
export const FormsTemplateSearch = <ITEM extends Bean>(props: FormsTemplatesSearchProps<ITEM>): JSX.Element => {
   const { register, watch } = useFormController<{ searchText: string | null }>({
      searchText: null,
   });

   const currentSearchText = watch('searchText');

   const { data: templatesExist, loading: templatesExistLoading, refetch: refetchTemplatesExist } = useCqrsExists(() =>
      props.queryCreator()
   );

   const [{ value: templates, loading: templatesLoading, error: templatesError }, handleTemplatesSearch] = useAsyncFn(
      async (searchText: string | null) => {
         return performCqrsQuerySearch<ITEM>(() => props.queryCreator(searchText));
      }
   );

   useUpdateEffect(() => {
      handleTemplatesSearch(currentSearchText);
   }, [templatesExist, currentSearchText]);

   const refetch = useCallback(async () => {
      if (!templatesExist || (templates && templates.length < 2)) {
         await refetchTemplatesExist();
      }
      await handleTemplatesSearch(currentSearchText);
   }, [templatesExist, templates, handleTemplatesSearch, currentSearchText, refetchTemplatesExist]);

   const loading = templatesLoading || templatesExistLoading;

   const childrenRenderProps = useMemo(
      () => ({
         templates: templates ?? [],
         refetch: refetch,
         loading: loading,
         templatesExist: Boolean(templatesExist),
      }),
      [loading, refetch, templates, templatesExist]
   );

   if (templatesError) {
      return (
         <Message
            testId={'fetchError'}
            type={MessageType.NEGATIVE}
            label={formsTemplatesTranslationKeys.templatesFetchError}
            displayIndicator
         />
      );
   }

   return (
      <>
         <StyledFormsTemplatesSearchInput direction={'column'} gap={'8px'} justifyContent={'center'}>
            {templatesExist ? (
               <ActionInput
                  testId={props.testId}
                  controller={register('searchText')}
                  placeholder={props.placeholder}
                  delay={FORMS_TEMPLATES_SEARCH_DEBOUNCE_TIME_IN_MS}
                  compact
               />
            ) : null}
            {loading ? <LoaderSpinner /> : null}
         </StyledFormsTemplatesSearchInput>

         {templatesExist && !loading && templates && templates.length === 0 ? (
            <FormsTemplatesListNoSearchResultsEmptyStatePlaceholder />
         ) : null}

         {props.children(childrenRenderProps)}
      </>
   );
};
